import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { compose } from 'redux';
import { injectIntl } from '../../../../../util/reactIntl';
import { useConfiguration } from '../../../../../context/configurationContext';
import { useRouteConfiguration } from '../../../../../context/routeConfigurationContext';
import { createResourceLocatorString } from '../../../../../util/routes';
import { createSlug } from '../../../../../util/urlHelpers';
import { generateCategorySlug, generateCitySlug } from '../../../../../util/seo';
import SearchMap from '../../../SearchMap/SearchMap';
import MapPreview from './MapPreview';
import { types as sdkTypes } from '../../../../../util/sdkLoader';
import css from './Map.module.css';

const { LatLng, Money, UUID } = sdkTypes;

const MODAL_BREAKPOINT = 1024;
const isWindowDefined = typeof window !== 'undefined';
const isDesktopLayout = isWindowDefined && window.innerWidth > MODAL_BREAKPOINT;

const transformItemsToOriginalListings = items => {
  const result = [];
  for (const item of items) {
    const { _geoloc, price, ...rest } = item;
    const hasValidGeo = _geoloc && typeof _geoloc.lat === 'number' && typeof _geoloc.lng === 'number'
      && !isNaN(_geoloc.lat) && !isNaN(_geoloc.lng);
    if (price && price.amount && price.currency && hasValidGeo) {
      result.push({
        id: new UUID(rest.objectID),
        type: 'listing',
        attributes: {
          geolocation: { ..._geoloc },
          price: new Money(price.amount, price.currency),
          ...rest,
        },
      });
    }
  }
  return result;
};

function MapComponent(props) {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const {
    currentUserCurrency,
    exchangeRates,
    intl,
    defaultOrigin,
    location,
    showMap,
    onClose,
    selectedBounds,
    activeListingId,
    algoliaListings,
    onToggleFullscreen,
    isFullscreen,
    currentUserCountryCode,
    panelOpenProgress = 0,
    mapPreviewVisible = true,
    onActivateListing,
    panelPointerHandlers = null,
  } = props;

  const [map, setMap] = useState(null);
  const [clickedListings, setClickedListings] = useState(null);
  const { lat, lng } = defaultOrigin || {};
  const latLng = useMemo(
    () => lat && lng && !isNaN(lat) && !isNaN(lng) ? new LatLng(lat, lng) : null,
    [lat, lng]
  );
  const listings = useMemo(
    () => transformItemsToOriginalListings(algoliaListings),
    [algoliaListings]
  );

  // Track previous search results key to detect when search results actually change
  const previousSearchResultsKeyRef = useRef(null);

  // Clear clickedListings and validate activeListingId when search results change
  useEffect(() => {
    const ids = listings.map(l => l.id.uuid).sort();
    const firstId = ids[0] || '';
    const currentKey = `${listings.length}:${firstId}:${ids.join(',')}`;
    const previousKey = previousSearchResultsKeyRef.current;

    if (previousKey !== null && currentKey !== previousKey) {
      setClickedListings(null);
    }

    previousSearchResultsKeyRef.current = currentKey;

    if (activeListingId && listings.length > 0) {
      const activeListingExists = listings.some(l => l.id.uuid === activeListingId.uuid);
      if (!activeListingExists && onActivateListing) {
        onActivateListing(null);
      }
    }
  }, [listings, activeListingId, onActivateListing]);

  const handleListingsClicked = useCallback((listings) => {
    setClickedListings(listings);
    // Clear activeListingId when user clicks on a map label
    if (onActivateListing) {
      onActivateListing(null);
    }
  }, []);

  const handleMapMoveEnd = useCallback((viewportBoundsChanged, { viewportBounds }) => {
    // On mobile, clear clickedListings when user interacts with map
    // so preview shows first listing from available listings
    const isMobile = isWindowDefined && window.innerWidth <= MODAL_BREAKPOINT;
    if (isMobile && viewportBoundsChanged) {
      setClickedListings(null);
    }

    // Call parent handler to refine search by map bounds
    // Map components pass viewportBounds in second parameter
    if (props.onMapMoveEnd && viewportBounds && viewportBoundsChanged) {
      props.onMapMoveEnd(viewportBounds);
    }
  }, [props.onMapMoveEnd]);

  const createURLToListing = useCallback((listing) => {
    const id = listing.id.uuid;
    const slug = createSlug(listing.attributes.title);
    const { category, city } = listing?.attributes?.publicData || {};
    const citySlug = generateCitySlug(city);
    const categorySlug = generateCategorySlug(category);
    const pathParams = {
      ...(citySlug ? { city: citySlug } : {}),
      ...(categorySlug ? { category: categorySlug } : {}),
      id,
      slug
    };

    // Capture current search URL to restore user's search state on "Rent More Items"
    const currentSearchUrl = typeof window !== 'undefined' ? window.location.pathname + window.location.search : '';

    const searchParams = {
      from: 'search',
      searchUrl: currentSearchUrl,
    };

    return createResourceLocatorString('ListingPage', routeConfiguration, pathParams, searchParams);
  }, [routeConfiguration]);

  const handleListingPreviewClicked = useCallback((listing) => {
    // Navigate to listing page
    const url = createURLToListing(listing);
    if (typeof window !== 'undefined' && window.location) {
      window.location.href = url;
    }
  }, [createURLToListing]);

  // Determine which listings to show in preview
  // Priority: 1. clickedListings (from group click), 2. activeListingId listing, 3. first listing
  let previewListings = null;
  if (clickedListings) {
    previewListings = Array.isArray(clickedListings) ? clickedListings : [clickedListings];
  } else if (activeListingId && listings.length > 0) {
    const activeListing = listings.find(l => l.id.uuid === activeListingId.uuid);
    previewListings = activeListing ? [activeListing] : (listings.length > 0 ? [listings[0]] : null);
  } else if (listings.length > 0) {
    previewListings = [listings[0]];
  }

  const isMobile = isWindowDefined && window.innerWidth <= MODAL_BREAKPOINT;

  return (
    <div className={css.mapWrapper}>
      {showMap || isDesktopLayout ? (
        <>
          <SearchMap
            reusableContainerClassName={css.map}
            activeListingId={activeListingId}
            bounds={selectedBounds}
            center={latLng}
            isSearchMapOpenOnMobile={false}
            location={location}
            listings={listings || []}
            onMapMoveEnd={handleMapMoveEnd}
            onCloseAsModal={() => {
              // onManageDisableScrolling("SearchPage.map", false);
            }}
            messages={intl.messages}
            selectedCurrency={currentUserCurrency}
            exchangeRates={exchangeRates}
            setMap={setMap}
            showCloseButton={!isDesktopLayout}
            onClose={onClose}
            onToggleFullscreen={onToggleFullscreen}
            isFullscreen={isFullscreen}
            onListingsClicked={handleListingsClicked}
          />
          {isMobile && showMap && mapPreviewVisible && previewListings && previewListings.length > 0 && exchangeRates && exchangeRates.length > 0 && (
            <MapPreview
              listings={previewListings}
              activeListingId={activeListingId}
              createURLToListing={createURLToListing}
              onListingPreviewClicked={handleListingPreviewClicked}
              config={config}
              selectedCurrency={currentUserCurrency}
              exchangeRates={exchangeRates}
              currentUserCountryCode={currentUserCountryCode}
              onClose={onClose}
              panelOpenProgress={panelOpenProgress}
              panelPointerHandlers={panelPointerHandlers}
            />
          )}
        </>
      ) : null}
    </div>
  );
}

export default compose(injectIntl)(MapComponent);
